import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  useAssemblePackage,
  useComplianceCalendar,
  useCompliancePackages,
  usePackageDecision,
} from '../hooks/useOps';
import { formatDate, formatIDR } from '../lib/format';
import { opsApi } from '../api/client';
import type { CalendarItem, CompliancePackage } from '../api/types';

/** Download the founder-gated PDF (auth header required, so not a plain <a href>). */
async function openPackagePdf(id: string): Promise<void> {
  const blob = await opsApi.compliancePackagePdf(id);
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener');
  // Revoke after the new tab has had a chance to load the blob.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

const STATE_COLOR: Record<string, 'default' | 'warning' | 'error' | 'success'> = {
  upcoming: 'default',
  due_soon: 'warning',
  overdue: 'error',
  done: 'success',
};
const STATE_LABEL: Record<string, string> = {
  upcoming: 'Mendatang',
  due_soon: 'Segera',
  overdue: 'Terlewat',
  done: 'Selesai (NTPN tercatat)',
};
const PKG_COLOR: Record<string, 'default' | 'info' | 'success'> = {
  draft: 'default',
  approved: 'info',
  handed_off: 'success',
};

function nextDeadline(items: CalendarItem[]): CalendarItem | undefined {
  const today = new Date().toISOString().slice(0, 10);
  return [...items]
    .filter((i) => i.dueDate >= today)
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate))[0];
}

export function CompliancePage() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [period, setPeriod] = useState(String(new Date().getFullYear()));
  const [handoffFor, setHandoffFor] = useState<CompliancePackage | null>(null);
  const [recipient, setRecipient] = useState('');

  const calendar = useComplianceCalendar(year);
  const packages = useCompliancePackages();
  const assemble = useAssemblePackage();
  const decide = usePackageDecision();

  const overdue = calendar.data?.items.filter((i) => i.state === 'overdue').length ?? 0;
  const next = calendar.data ? nextDeadline(calendar.data.items) : undefined;

  return (
    <Box>
      <Typography variant="h5" component="h1" fontWeight={700} mb={2}>
        Kepatuhan & Pajak
      </Typography>

      {/* "Am I OK?" summary */}
      <Grid container spacing={2} mb={3}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="overline" color="text.secondary">
              Status
            </Typography>
            <Typography variant="h6" fontWeight={700} color={overdue ? 'error.main' : 'success.main'}>
              {overdue ? `${overdue} kewajiban terlewat` : 'Tidak ada yang terlewat'}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="overline" color="text.secondary">
              Tenggat berikutnya
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              {next ? formatDate(next.dueDate) : '—'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {next?.label ?? 'Tidak ada'}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="overline" color="text.secondary">
              Rezim pajak {year}
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              {calendar.data?.finalPphActive ? 'PPh Final 0,5%' : 'PPN + PPh Badan'}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Calendar */}
      <Stack direction="row" alignItems="center" gap={1} mb={1}>
        <Typography variant="h6" fontWeight={600}>
          Kalender Kewajiban
        </Typography>
        <Box flexGrow={1} />
        <TextField
          size="small"
          type="number"
          label="Tahun"
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          sx={{ width: 120 }}
        />
      </Stack>
      <Paper variant="outlined" sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Kewajiban</TableCell>
              <TableCell>Periode</TableCell>
              <TableCell>Tenggat</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(calendar.data?.items ?? []).map((i, idx) => (
              <TableRow key={`${i.kind}-${i.coversPeriod}-${idx}`}>
                <TableCell>{i.label}</TableCell>
                <TableCell>{i.coversPeriod}</TableCell>
                <TableCell>{formatDate(i.dueDate)}</TableCell>
                <TableCell>
                  <Chip size="small" color={STATE_COLOR[i.state]} label={STATE_LABEL[i.state]} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* Handoff packages */}
      <Stack direction="row" alignItems="center" gap={1} mb={1}>
        <Typography variant="h6" fontWeight={600}>
          Paket untuk Konsultan
        </Typography>
        <Box flexGrow={1} />
        <TextField
          size="small"
          label="Periode (YYYY / YYYY-MM)"
          value={period}
          onChange={(e) => setPeriod(e.target.value.trim())}
          sx={{ width: 190 }}
        />
        <Button
          variant="contained"
          onClick={() => assemble.mutate(period)}
          disabled={assemble.isPending || !/^\d{4}(-\d{2})?$/.test(period)}
        >
          {assemble.isPending ? 'Menyusun…' : 'Susun paket'}
        </Button>
      </Stack>
      <Alert severity="info" sx={{ mb: 2 }}>
        Menyusun paket hanya menyiapkan dokumen untuk konsultan pajak Anda. <strong>Tidak ada yang dilaporkan ke
        DJP.</strong> Pelepasan ke konsultan (handoff) butuh persetujuan manual.
      </Alert>
      {decide.error && <Alert severity="error" sx={{ mb: 2 }}>{(decide.error as Error).message}</Alert>}

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Periode</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Kelengkapan</TableCell>
              <TableCell>Dokumen</TableCell>
              <TableCell align="right">Aksi</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(packages.data ?? []).length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary" py={1}>
                    Belum ada paket. Susun satu untuk periode di atas.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              packages.data!.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>{p.period}</TableCell>
                  <TableCell>
                    <Chip size="small" color={PKG_COLOR[p.status]} label={p.status.replace('_', ' ')} />
                    {p.handedOffTo && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        → {p.handedOffTo} ({formatDate(p.handedOffAt)})
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {p.incomplete ? (
                      <Chip size="small" color="warning" label={`${p.missing?.length ?? 0} gap`} />
                    ) : (
                      <Chip size="small" color="success" label="Lengkap" />
                    )}
                  </TableCell>
                  <TableCell>
                    {p.documentUrl ? (
                      <Button size="small" onClick={() => void openPackagePdf(p.id)}>
                        Buka PDF
                      </Button>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {p.status === 'draft' && (
                      <Button
                        size="small"
                        disabled={p.incomplete || decide.isPending}
                        onClick={() => decide.mutate({ id: p.id, action: 'approve' })}
                      >
                        Setujui
                      </Button>
                    )}
                    {p.status === 'approved' && (
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => {
                          setHandoffFor(p);
                          setRecipient('');
                        }}
                      >
                        Handoff
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Handoff dialog */}
      <Dialog open={Boolean(handoffFor)} onClose={() => setHandoffFor(null)} fullWidth maxWidth="xs">
        <DialogTitle>Handoff paket {handoffFor?.period}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Ini menyerahkan dokumen ke konsultan pajak Anda. Tidak ada yang dilaporkan ke DJP.
          </Alert>
          <TextField
            autoFocus
            fullWidth
            label="Nama konsultan / KKP"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHandoffFor(null)}>Batal</Button>
          <Button
            variant="contained"
            disabled={!recipient.trim() || decide.isPending}
            onClick={() =>
              decide.mutate(
                { id: handoffFor!.id, action: 'handoff', handedOffTo: recipient.trim() },
                { onSuccess: () => setHandoffFor(null) },
              )
            }
          >
            Konfirmasi handoff
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
